import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { useChurch } from '../hooks/useChurches'
import {
  useSubscriptionStatus,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useCancelSubscription,
  useResumeSubscription,
  useSwitchPlan,
} from '../hooks/useChurchAdmin'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'

type Tier = 'starter' | 'community' | 'connection'
type Cadence = 'monthly' | 'annual'

const TIERS: {
  id: Tier
  name: string
  sub: string
  monthly: number
  annual: number
  features: string[]
  highlight?: boolean
}[] = [
  {
    id: 'starter',
    name: 'Starter',
    sub: 'For small churches',
    monthly: 9,
    annual: 7,
    features: ['1 coordinator seat', 'Up to 5 events/month', 'RSVP management', 'Church profile page'],
  },
  {
    id: 'community',
    name: 'Community',
    sub: 'Most churches',
    monthly: 29,
    annual: 23,
    features: ['5 coordinator seats', 'Unlimited events', 'RSVP management', 'Bulletin inserts & broadcast', 'Day-of check-in'],
    highlight: true,
  },
  {
    id: 'connection',
    name: 'Connection',
    sub: 'Multi-campus churches',
    monthly: 79,
    annual: 63,
    features: ['Unlimited coordinator seats', 'Unlimited events', 'Multiple campuses', 'All Community features', 'Priority support'],
  },
]

const TIER_LABEL: Record<Tier, string> = { starter: 'Starter', community: 'Community', connection: 'Connection' }

export default function BillingPage() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const checkoutSuccess = searchParams.get('checkout') === 'success'
  const checkoutSessionId = searchParams.get('session_id')
  const queryClient = useQueryClient()

  const { data: churchData } = useChurch(slug)
  const church = churchData?.data
  const { data: subData, isLoading } = useSubscriptionStatus(church?.id)
  const createCheckout = useCreateCheckoutSession()
  const createPortal = useCreatePortalSession()
  const cancelSub = useCancelSubscription()
  const resumeSub = useResumeSubscription()
  const switchPlan = useSwitchPlan()

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null)
  const [cadence, setCadence] = useState<Cadence>('monthly')

  useEffect(() => {
    if (!checkoutSuccess || !church?.id || !checkoutSessionId) return
    const refetch = () => void queryClient.invalidateQueries({ queryKey: ['churches', church.id, 'subscription'] })
    void apiFetch('/billing/sync-checkout', {
      method: 'POST',
      body: JSON.stringify({ sessionId: checkoutSessionId }),
    }).then(refetch).catch(() => { refetch() })
    const t1 = setTimeout(refetch, 3000)
    return () => { clearTimeout(t1) }
  }, [checkoutSuccess, checkoutSessionId, church?.id, queryClient])

  const subscription = subData?.data
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing'
  const isTrialing = subscription?.status === 'trialing'
  const isCancelling = subscription?.cancelAtPeriodEnd === true
  const currentTier = subscription?.tier ?? null

  const renewalDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  const daysLeft = subscription?.currentPeriodEnd
    ? Math.ceil((new Date(subscription.currentPeriodEnd).getTime() - Date.now()) / 86_400_000)
    : null

  const activeTier = selectedTier ?? (currentTier as Tier | null)
  const tierData = TIERS.find(t => t.id === activeTier)

  function handleTierSelect(tier: Tier) {
    setSelectedTier(tier)
  }

  function handleSubscribe() {
    if (!church || !activeTier) return
    if (isActive && currentTier) {
      switchPlan.mutate({ churchId: church.id, tier: activeTier, plan: cadence })
    } else {
      createCheckout.mutate({ churchId: church.id, tier: activeTier, plan: cadence })
    }
  }

  const isSamePlan = isActive && activeTier === currentTier && cadence === subscription?.plan

  return (
    <AdminShell>
      {checkoutSuccess && (
        <div className="mb-5 px-4 py-3.5 rounded-xl bg-good-soft border border-good-line flex items-center gap-3 text-good-deep text-[13.5px]">
          <span className="text-[18px]">🎉</span>
          <div>
            <span className="font-semibold">You're subscribed!</span> Your trial has started — you can create events right away.
          </div>
        </div>
      )}

      <AdminPageHeader
        eyebrow="Billing"
        title="Billing &"
        titleItalic="subscription."
        sub="One subscription covers your whole church. Members and attendees are always free."
        actions={
          isActive
            ? <AdmBtn variant="ghost" icon="arrow" onClick={() => church && createPortal.mutate(church.id)} disabled={createPortal.isPending || !church}>
                {createPortal.isPending ? 'Loading…' : 'Manage in Stripe'}
              </AdmBtn>
            : undefined
        }
      />

      {isLoading && <div className="text-[13px] text-muted mb-6">Loading…</div>}

      {!isLoading && (
        <div className="flex flex-col gap-5">

          {/* ── Active plan hero ─────────────────────────────────────────── */}
          {isActive && (
            <AdminCard padding={0}>
              <div className="px-7 py-6 rounded-t-[inherit]" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2060 100%)' }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/20 text-white/70 text-[11px] font-semibold tracking-wide mb-3">
                      <span className={`w-1.5 h-1.5 rounded-full ${isTrialing ? 'bg-yellow-400' : 'bg-emerald-400'}`} />
                      {isTrialing ? `FREE TRIAL · ${daysLeft} DAYS LEFT` : isCancelling ? 'CANCELS SOON' : 'ACTIVE'}
                    </div>
                    <div className="serif text-white text-[28px] tracking-[-0.02em] leading-[1.1]">
                      {currentTier ? TIER_LABEL[currentTier as Tier] : ''} plan
                    </div>
                    <div className="text-white/60 text-[13px] mt-1.5">
                      {TIERS.find(t => t.id === currentTier)?.features.join(' · ')}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-white/50 text-[10px] font-semibold tracking-widest uppercase mb-1">
                      {isCancelling ? 'Ends' : isTrialing ? 'Then' : 'Renews at'}
                    </div>
                    <div className="text-white serif text-[36px] tracking-[-0.02em] leading-none">
                      ${tierData ? (subscription?.plan === 'annual' ? tierData.annual : tierData.monthly) : '—'}
                      <span className="text-[16px] font-normal text-white/60">/mo</span>
                    </div>
                    <div className="text-white/50 text-[11.5px] mt-1">
                      {subscription?.plan === 'annual' ? 'Billed annually' : 'Billed monthly'}{renewalDate ? ` · ${isCancelling ? 'ends' : 'renews'} ${renewalDate}` : ''}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-7 py-4 flex items-center gap-3 border-t border-line-soft">
                {showCancelConfirm ? (
                  <div className="flex items-center gap-3 flex-wrap w-full">
                    <span className="text-[13px] text-ink-2">Access ends <strong>{renewalDate}</strong>. Continue?</span>
                    <button
                      onClick={() => church && cancelSub.mutate(church.id, { onSuccess: () => setShowCancelConfirm(false) })}
                      disabled={cancelSub.isPending}
                      className="h-[32px] px-3.5 rounded-full bg-danger text-white border-0 text-[12.5px] font-semibold cursor-pointer disabled:opacity-50"
                    >
                      {cancelSub.isPending ? 'Cancelling…' : 'Yes, cancel'}
                    </button>
                    <button onClick={() => setShowCancelConfirm(false)} className="h-[32px] px-3.5 rounded-full bg-transparent border border-line text-ink text-[12.5px] font-semibold cursor-pointer">
                      Keep subscription
                    </button>
                  </div>
                ) : isCancelling ? (
                  <button
                    onClick={() => church && resumeSub.mutate(church.id)}
                    disabled={resumeSub.isPending || !church}
                    className="h-[34px] px-4 rounded-full bg-primary text-on-primary border-0 text-[13px] font-semibold cursor-pointer disabled:opacity-50"
                  >
                    {resumeSub.isPending ? 'Resuming…' : 'Resume subscription'}
                  </button>
                ) : (
                  <button className="text-[12px] text-muted bg-transparent border-0 cursor-pointer underline underline-offset-2" onClick={() => setShowCancelConfirm(true)}>
                    Cancel subscription
                  </button>
                )}
              </div>
            </AdminCard>
          )}

          {/* ── Step 1: Tier grid ────────────────────────────────────────── */}
          <AdminCard title={isActive ? 'Change plan' : 'Choose a plan'} action={
            <span className="mono text-[10px] text-muted tracking-[0.08em]">SAVE 20% ANNUALLY</span>
          }>
            {!isActive && (
              <div className="flex items-center gap-1.5 text-[12.5px] text-muted mb-4">
                <AdmIcn name="check" size={13} />
                14-day free trial · no credit card required to start
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {TIERS.map((tier) => {
                const isSelected = activeTier === tier.id
                const isCurrent = currentTier === tier.id
                return (
                  <button
                    key={tier.id}
                    onClick={() => handleTierSelect(tier.id)}
                    className={`p-5 rounded-xl border-[1.5px] text-left relative cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary bg-primary-soft'
                        : 'border-line-soft bg-s2 hover:border-line'
                    }`}
                  >
                    {isCurrent && !isSelected && (
                      <span className="absolute -top-[10px] left-4 px-2.5 py-[3px] rounded-full bg-ink text-canvas text-[10px] font-semibold">
                        Current
                      </span>
                    )}
                    {isSelected && (
                      <span className="absolute -top-[10px] left-4 px-2.5 py-[3px] rounded-full bg-primary text-on-primary text-[10px] font-semibold">
                        {isCurrent ? 'Current' : 'Selected'}
                      </span>
                    )}
                    {tier.highlight && !isSelected && !isCurrent && (
                      <span className="absolute -top-[10px] left-4 px-2.5 py-[3px] rounded-full bg-accent text-white text-[10px] font-semibold">
                        Popular
                      </span>
                    )}
                    <div className={`text-[13px] font-semibold mb-0.5 ${isSelected ? 'text-primary-deep' : 'text-ink'}`}>
                      {tier.name}
                    </div>
                    <div className="text-[11.5px] text-muted mb-3">{tier.sub}</div>
                    <div className="flex items-baseline gap-0.5 mb-3">
                      <span className="serif text-[28px] tracking-[-0.02em]">${tier.monthly}</span>
                      <span className="text-[12px] text-muted">/mo</span>
                    </div>
                    <ul className="space-y-1.5">
                      {tier.features.map(f => (
                        <li key={f} className="flex items-start gap-2 text-[12px] text-ink-2">
                          <span className={`mt-[2px] shrink-0 ${isSelected ? 'text-primary' : 'text-good'}`}><AdmIcn name="check" size={12} /></span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </button>
                )
              })}
            </div>
          </AdminCard>

          {/* ── Step 2: Cadence + CTA (shown after tier selected) ───────── */}
          {activeTier && (
            <AdminCard title="Billing frequency">
              <div className="grid grid-cols-2 gap-3 mb-4">
                {(['monthly', 'annual'] as Cadence[]).map((c) => {
                  const price = c === 'annual' ? tierData!.annual : tierData!.monthly
                  const isSelectedCadence = cadence === c
                  return (
                    <button
                      key={c}
                      onClick={() => setCadence(c)}
                      className={`p-4 rounded-xl border-[1.5px] text-left cursor-pointer transition-all ${
                        isSelectedCadence ? 'border-primary bg-primary-soft' : 'border-line-soft bg-s2 hover:border-line'
                      }`}
                    >
                      {c === 'annual' && (
                        <span className="inline-block mb-1.5 px-2 py-[2px] rounded-full bg-good text-on-primary text-[10.5px] font-semibold">
                          Save 20%
                        </span>
                      )}
                      <div className={`text-[13.5px] font-semibold mb-0.5 ${isSelectedCadence ? 'text-primary-deep' : 'text-ink'}`}>
                        {c === 'monthly' ? 'Monthly' : 'Annual'}
                      </div>
                      <div className="flex items-baseline gap-0.5 mb-1">
                        <span className="serif text-[26px] tracking-[-0.02em]">${price}</span>
                        <span className="text-[13px] text-muted">/mo</span>
                      </div>
                      <div className="text-[11.5px] text-muted">
                        {c === 'monthly'
                          ? 'Billed monthly · cancel any time'
                          : `$${tierData!.annual * 12}/yr · saves $${(tierData!.monthly - tierData!.annual) * 12}`}
                      </div>
                    </button>
                  )
                })}
              </div>

              {isSamePlan ? (
                <div className="flex items-center gap-2 text-[13px] text-muted p-3.5 rounded-xl bg-s2 border border-line-soft">
                  <AdmIcn name="check" size={14} />
                  This is your current plan
                </div>
              ) : (
                <button
                  onClick={handleSubscribe}
                  disabled={createCheckout.isPending || switchPlan.isPending || !church}
                  className="w-full h-[44px] rounded-full bg-primary text-on-primary border-0 text-[14px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(createCheckout.isPending || switchPlan.isPending)
                    ? 'Loading…'
                    : isActive
                      ? `Switch to ${TIER_LABEL[activeTier]} ${cadence}`
                      : `Start free trial — ${TIER_LABEL[activeTier]} ${cadence}`}
                </button>
              )}

              {!isActive && (
                <div className="mt-2.5 text-center text-[11.5px] text-muted">
                  Payments processed securely by Stripe. Cancel any time during the trial.
                </div>
              )}
            </AdminCard>
          )}

          {/* ── Hardship note ────────────────────────────────────────────── */}
          <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-s2 border border-line-soft">
            <AdmIcn name="shield" size={16} />
            <div className="text-[12px] text-ink-2">
              <strong>Hardship pricing</strong> available for small churches —{' '}
              <span className="text-primary underline underline-offset-2 cursor-pointer">email us</span>.
            </div>
          </div>

        </div>
      )}
    </AdminShell>
  )
}
